import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Source, CreateSourceInput, UpdateSourceInput } from '../model/contracts'
import { Button } from '../../../shared/ui/Button'

interface SourceFormProps {
  source?: Source
  onSubmit: (data: CreateSourceInput | UpdateSourceInput) => void
  onCancel?: () => void
}

import { PLATFORM_OPTIONS } from '../../posts/model/platforms'

import { Switch } from '../../../shared/ui/Switch'

export function SourceForm({ source, onSubmit, onCancel }: SourceFormProps) {
  const { t } = useTranslation()
  const [platform, setPlatform] = useState(source?.platform || '')
  const [url, setUrl] = useState(source?.url || '')
  const [description, setDescription] = useState(source?.description || '')
  const [isEnabled, setIsEnabled] = useState(source?.isEnabled ?? true)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!url.trim()) return

    if (source) {
      onSubmit({
        id: source.id,
        name: source.name,
        platform,
        url,
        description,
        isEnabled,
      })
    } else {
      onSubmit({
        name: '',
        platform,
        url,
        description,
        isEnabled,
      })
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div>
        <label
          htmlFor="source-url"
          style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
        >
          URL *
        </label>
        <input
          id="source-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          required
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 14,
          }}
          placeholder="https://facebook.com/username or https://x.com/username"
        />
      </div>

      <div>
        <label
          htmlFor="source-platform"
          style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
        >
          {t('sources.platform')}
        </label>
        <select
          id="source-platform"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 14,
            height: '40px',
          }}
        >
          <option value="">{t('sources.selectPlatform')}</option>
          {PLATFORM_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {t(`platforms.${p}`)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="source-description"
          style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
        >
          {t('sources.description')}
        </label>
        <textarea
          id="source-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 14,
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
          placeholder={t('sources.descriptionPlaceholder')}
        />
      </div>

      <Switch
        id="source-enabled"
        checked={isEnabled}
        onChange={setIsEnabled}
        label={t('sources.isEnabled')}
      />

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button type="button" variant="neutral" onClick={onCancel}>
            {t('sources.cancel')}
          </Button>
        )}
        <Button type="submit">
          {source ? t('sources.updateSource') : t('sources.createSource')}
        </Button>
      </div>
    </form>
  )
}
